import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for error tracking and monitoring
 * Only initializes if DSN is provided in environment variables
 */
export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';

  // Only initialize if DSN is provided
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] DSN not configured. Error tracking disabled.');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment,
    
    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text content for privacy
        maskAllText: true,
        // Block all media for privacy
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring
    // Sample 10% of transactions in production, 100% in development
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session Replay
    // Sample 10% of sessions in production, 100% in development
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Always record replays when errors occur
    replaysOnErrorSampleRate: 1.0,

    // Release tracking (optional - can be set via CI/CD)
    release: import.meta.env.VITE_APP_VERSION || undefined,

    // Filter and modify events before sending
    beforeSend(event, hint) {
      // Log events in development for debugging
      if (import.meta.env.DEV) {
        console.log('[Sentry] Event captured:', event);
      }

      // Filter out known non-critical errors
      if (event.exception) {
        const error = hint.originalException || hint.syntheticException;
        const errorMessage = error?.message || '';

        // Filter out browser extension errors
        if (
          errorMessage.includes('chrome-extension://') ||
          errorMessage.includes('moz-extension://') ||
          errorMessage.includes('safari-extension://')
        ) {
          return null; // Don't send this event
        }

        // Filter out network errors that are expected (e.g., CORS, aborted requests)
        if (
          errorMessage.includes('Failed to fetch') &&
          errorMessage.includes('CORS')
        ) {
          return null;
        }
      }

      return event;
    },

    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',
      // Network errors that are expected
      'NetworkError',
      'Network request failed',
      // Third-party scripts
      'Script error',
    ],
  });

  if (import.meta.env.DEV) {
    console.log('[Sentry] Initialized successfully for environment:', environment);
  }
};

/**
 * Set user context in Sentry
 * @param {Object} user - User object with id, email, username, etc.
 */
export const setUserContext = (user) => {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.user_id || user.id,
    email: user.email,
    username: user.userName || user.username,
    // Add any other user properties you want to track
  });
};

/**
 * Clear user context (e.g., on logout)
 */
export const clearUserContext = () => {
  Sentry.setUser(null);
};

/**
 * Add breadcrumb for navigation
 * @param {string} from - Previous route
 * @param {string} to - New route
 */
export const addNavigationBreadcrumb = (from, to) => {
  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `Navigated from ${from} to ${to}`,
    level: 'info',
    data: {
      from,
      to,
    },
  });
};

